import { useTranslation } from 'react-i18next';
import { Infinity as InfinityIcon, Headphones, CalendarDays, ExternalLink } from 'lucide-react';
import { Icon, ShieldCheck } from '../ui/Icon';

function FeaturePill({ icon: IconComp, text }: { icon: React.ElementType; text: string }) {
  return (
    <div className="flex items-center gap-1.5 bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-full px-3 py-1.5">
      <Icon as={IconComp} size={12} className="text-slate-400 shrink-0" />
      <span className="text-xs text-slate-600 dark:text-slate-400 whitespace-nowrap">{text}</span>
    </div>
  );
}

export default function ServicesConnectivity({ destination }: { destination?: string }) {
  const { t } = useTranslation();

  const country = destination
    ? (destination.includes(',') ? destination.split(',').at(-1)!.trim() : destination)
    : undefined;

  return (
    <>
      {/* Section label */}
      <div className="flex items-center gap-3 mb-5">
        <span className="font-mono text-[10px] tracking-[0.14em] uppercase text-slate-400 whitespace-nowrap">
          {t('services.connectivity.label')}
        </span>
        <div className="flex-1 h-px bg-slate-100 dark:bg-slate-800" />
        {country && (
          <span className="font-mono text-[10px] tracking-[0.14em] uppercase text-slate-400">
            {country.toUpperCase()}
          </span>
        )}
      </div>

      <h3 className="font-serif italic font-semibold text-xl md:text-2xl text-slate-900 dark:text-slate-100 mb-1.5">
        {t('services.connectivity.heading')}
      </h3>
      <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed mb-8">
        {t('services.connectivity.body')}
      </p>

      {/* ── Holafly card ────────────────────────────────────────────────── */}
      <a
        href="https://holafly.sjv.io/c/7309003/3856277/24764"
        target="_blank"
        rel="noopener noreferrer"
        className="block rounded-2xl border border-slate-100 dark:border-slate-700 overflow-hidden hover:shadow-lg transition-shadow duration-200 mb-5"
      >
        {/* Orange accent bar */}
        <div className="h-1 bg-gradient-to-r from-orange-400 via-orange-500 to-amber-400" />

        <div className="bg-white dark:bg-slate-800/60 p-6 md:p-8">

          {/* Top row: logo + badge */}
          <div className="flex items-start justify-between mb-5">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-orange-500 flex items-center justify-center shrink-0">
                <span className="text-white font-bold text-base">h</span>
              </div>
              <div>
                <p className="font-semibold text-sm text-slate-900 dark:text-slate-100">Holafly</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">{t('services.connectivity.holafly.subtitle')}</p>
              </div>
            </div>
            <span className="text-[10px] font-mono tracking-[0.12em] uppercase border border-amber-200 dark:border-amber-700/40 text-amber-600 dark:text-amber-500 px-2.5 py-1 rounded-full whitespace-nowrap">
              {t('services.connectivity.editorsPick')}
            </span>
          </div>

          {/* Description */}
          <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed mb-5">
            {t('services.connectivity.holafly.description')}
          </p>

          {/* Feature pills */}
          <div className="flex flex-wrap gap-2 mb-6">
            <FeaturePill icon={InfinityIcon} text={t('services.connectivity.holafly.feature1')} />
            <FeaturePill icon={Headphones}   text={t('services.connectivity.holafly.feature2')} />
            <FeaturePill icon={CalendarDays} text={t('services.connectivity.holafly.feature3')} />
          </div>

          {/* Discount code */}
          <div className="flex items-center gap-2.5 bg-orange-50 dark:bg-orange-900/20 border border-orange-100 dark:border-orange-800/30 rounded-xl px-4 py-3 mb-6">
            <div className="w-2 h-2 rounded-full bg-orange-400 shrink-0" />
            <span className="font-mono text-xs font-bold tracking-widest text-orange-600 dark:text-orange-400">GROMLY</span>
            <span className="text-xs text-slate-500 dark:text-slate-400">{t('services.connectivity.holafly.discountNote')}</span>
          </div>

          {/* Bottom: price + CTA */}
          <div className="flex items-center justify-between pt-5 border-t border-slate-100 dark:border-slate-700">
            <div>
              <p className="text-[10px] font-mono tracking-[0.12em] uppercase text-slate-400 mb-0.5">
                {t('services.connectivity.from')}
              </p>
              <p className="font-serif text-2xl text-slate-900 dark:text-slate-100 leading-none">
                {t('services.connectivity.holafly.price')}
                <span className="text-xs font-sans text-slate-500 dark:text-slate-400 ml-1">
                  {country
                    ? t('services.connectivity.holafly.priceNote', { destination: country })
                    : t('services.connectivity.holafly.priceNoteGeneric')}
                </span>
              </p>
            </div>
            <span className="flex items-center gap-1.5 bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 text-xs font-medium px-5 py-2.5 rounded-xl whitespace-nowrap">
              {t('services.connectivity.viewPlans')}
              <Icon as={ExternalLink} size={11} />
            </span>
          </div>
        </div>
      </a>

      {/* ── Partnership note ────────────────────────────────────────────── */}
      <div className="flex items-start gap-2.5 bg-slate-50 dark:bg-slate-800/60 border border-slate-100 dark:border-slate-800 rounded-xl px-4 py-3.5">
        <Icon as={ShieldCheck} size={14} className="text-slate-400 shrink-0 mt-0.5" />
        <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
          <span className="font-semibold text-slate-700 dark:text-slate-200">{t('services.partnership')}</span>{' '}
          {t('services.partnershipNote')}
        </p>
      </div>
    </>
  );
}
